import 'isomorphic-fetch';
import defaultsDeep from 'lodash/defaultsDeep';
import { buildHeadersFromRequest } from '../utils/requestHelper';
import configUtil from '../utils/config';
import { ORDER_HISTORY } from '../actions/types';
import sentry from '../utils/sentry';
import jsonApiParser from './jsonApiParser';

const { Sentry, captureException } = sentry();

/*
  A middleware for making requests to the deliveroo API
  =====================================================

  Any action that returns an object that has the following shape will be picked up by this function

  {
    type: 'LOGIN',
    endpoint: '/orderapp/v1/login', // the hook for this middleware.
    authorized: false,              // send autorization headers?
    options: {
      ...fetchOptions,
    },
    onSuccess: (res) => undefined,
    onFailure: (res) => undefined,
  }

*/

const JSON_MEDIA_TYPE = 'application/json';
const JSONAPI_MEDIA_TYPE = 'application/vnd.api+json';

const defaultOptions = {
  headers: {
    'content-type': JSON_MEDIA_TYPE,
    accept: [JSON_MEDIA_TYPE, JSONAPI_MEDIA_TYPE].join(', '),
  },
  credentials: 'omit',
  // Set an aggressive default timeout, to prevent outbound calls queueing
  timeout: 5000,
};

export const JSONResponseHandler = (response, action) => (data) => {
  const isServer = typeof window === 'undefined';
  let parsedData;
  try {
    parsedData = JSON.parse(data);
  } catch (error) {
    // If the JSON fails to parse report an error to Sentry and add some additional context for debugging. Then return a promise rejection.
    const err = new Error(
      `API Middleware - ${isServer ? 'Server' : 'Browser'}: Failed To Parse JSON`,
    );

    Sentry.withScope((scope) => {
      scope.setExtras({
        action: action.type,
        status: response.status,
        error,
        data,
      });

      captureException(err);
    });

    return Promise.reject(err);
  }

  if (!parsedData) {
    // If the JSON successfully parses but the data is a falsey value, i.e null, undefined, empty string.
    // Report the error to Sentry and return a promise rejection as these values are likely to crash in the Reducers.
    const err = new Error(
      `API Middleware - ${isServer ? 'Server' : 'Browser'}: Invalid JSON Response`,
    );
    Sentry.withScope((scope) => {
      scope.setExtras({
        action: action.type,
        status: response.status,
        data,
      });

      captureException(err);
    });
    return Promise.reject(err);
  }

  // If the JSON parses successfully and there is a body of data then return the following block.
  return {
    payload: { ...parsedData },
    status: response.status,
    headers: response.headers,
  };
};

export const createExceptionHandler = (next, action) => (error) => {
  const isError = error instanceof Error;
  const isServerFetchError = error.name === 'FetchError';
  // Only throw the error on failed Browser requests to avoid rendering the error page mid user journey.
  const isJSONParseServerError = /API Middleware - Server/i.test(error.message);

  if (isError && !isServerFetchError && !isJSONParseServerError) {
    throw error;
  }

  let status = error.status || 500;
  const data = error.data || {};

  if (isServerFetchError) {
    if (error.type === 'request-timeout') {
      status = 504;
    }
  }

  next({
    type: `${action.type}_FAILURE`,
    status,
    message: data.message || error.message,
    payload: {
      ...data,
    },
  });

  if (action.onFailure) action.onFailure(data);
};

const api = (store) => (next) => (action) => {
  // If the object doesn't have an endpoint, pass it off.
  if (!action.endpoint) return next(action);

  const options = {};
  const { request, config } = store.getState();
  const requestOptions = { headers: buildHeadersFromRequest({ request, config }) };

  defaultsDeep(options, action.options, requestOptions, defaultOptions);

  next({ type: `${action.type}_REQUEST` });

  if (options.headers['content-type'] === JSON_MEDIA_TYPE && options.body) {
    options.body = JSON.stringify(options.body);
  }

  // Default to the orderweb API host unless an action overrides
  const host = action.host || configUtil.orderappApiHost;

  if (!host) {
    throw new Error('Unable to find valid API host for fetch');
  }
  const url = `${host}${action.endpoint}`;

  return fetch(url, options)
    .then((response) => {
      if (!response.ok) {
        // If the response is not okay and we don't recieve json content return data as undefined.
        const contentType = response.headers.get('content-type');
        const contentLength = response.headers.get('content-length');

        if (contentLength === '0') {
          // eslint-disable-next-line prefer-promise-reject-errors
          return Promise.reject({
            data: { message: response.statusText },
            status: response.status,
          });
        }

        if (contentType && contentType.indexOf(JSON_MEDIA_TYPE) !== -1) {
          return response
            .json()
            .catch(
              // eslint-disable-next-line prefer-promise-reject-errors
              (err) => Promise.reject({ data: err, status: response.status }),
            )
            .then(
              // eslint-disable-next-line prefer-promise-reject-errors
              (data) => Promise.reject({ data, status: response.status }),
            );
        }

        // eslint-disable-next-line prefer-promise-reject-errors
        return Promise.reject({ data: undefined, status: response.status });
      }

      if (response.status === 204) {
        return {
          payload: {},
          status: response.status,
          headers: response.headers,
        };
      }

      return response.text().then(JSONResponseHandler(response, action));
    })
    .then((response) => {
      const contentType = response.headers.get('content-type');
      if (contentType === JSONAPI_MEDIA_TYPE) {
        return {
          ...response,
          payload: jsonApiParser(response.payload),
        };
      }

      return response;
    })
    .then((response) => {
      const requestKeys = action.payload ? Object.keys(action.payload) : [];
      const responseKeys = response.payload ? Object.keys(response.payload) : [];

      requestKeys.filter((key) => responseKeys.indexOf(key) !== -1).forEach((key) =>
        // eslint-disable-next-line no-console
        console.warn(`API middleware: clashing keys in the payload field. Overriding: ${key}`),
      );

      const newAction = {
        type: `${action.type}_SUCCESS`,
        status: response.status,
        payload: {
          ...action.payload,
          ...response.payload,
        },
        meta: {
          apiMiddleware: action,
        },
      };

      // Adding Sentry logging to try and identify a bug where order history seems to fail but still hits this success block.
      if (
        action.type === ORDER_HISTORY &&
        (!newAction.payload.orders || !newAction.payload.orders.length)
      ) {
        Sentry.withScope((scope) => {
          scope.setExtras({
            payload: newAction.payload,
            status: response.status,
            response,
          });
          scope.setLevel('debug');

          captureException(new Error('API Middleware: Order History API'));
        });
      }

      next(newAction);

      if (action.onSuccess) action.onSuccess(newAction);
    })
    .catch(createExceptionHandler(next, action));
};

export default api;
